import { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";

import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import { shouldFileBeVisible } from "../../utility";

const fileTypeDetails = [
    {
        matches: (type) => type.includes("video"),
        label: "Video",
        icon: MovieOutlinedIcon,
        color: "#1976d2",
        background: "rgba(25, 118, 210, 0.1)",
    },
    {
        matches: (type) => type.includes("image"),
        label: "Image",
        icon: ImageOutlinedIcon,
        color: "#7b1fa2",
        background: "rgba(123, 31, 162, 0.1)",
    },
    {
        matches: (type) => type === "application/pdf",
        label: "PDF",
        icon: PictureAsPdfOutlinedIcon,
        color: "#d32f2f",
        background: "rgba(211, 47, 47, 0.1)",
    },
    {
        matches: (type) => type.includes("audio"),
        label: "Audio",
        icon: AudiotrackOutlinedIcon,
        color: "#ed6c02",
        background: "rgba(237, 108, 2, 0.11)",
    },
    {
        matches: (type) => type === "link",
        label: "Link",
        icon: LinkOutlinedIcon,
        color: "#00838f",
        background: "rgba(0, 131, 143, 0.1)",
    },
];

const defaultTypeDetails = {
    label: "File",
    icon: InsertDriveFileOutlinedIcon,
    color: "#546e7a",
    background: "rgba(84, 110, 122, 0.11)",
};

function getFileTypeDetails(file) {
    const type = file?.type || "";
    return (
        fileTypeDetails.find((details) => details.matches(type)) ||
        defaultTypeDetails
    );
}

function getPreviewUrl(file) {
    const type = file?.type || "";

    if (type.includes("video")) {
        return `https://vz-db0e7359-d70.b-cdn.net/${
            file.videoKey || file.key
        }/thumbnail.jpg`;
    }
    if (type.includes("image")) {
        return file.url;
    }
    if (type === "application/pdf") {
        return file.thumbnail;
    }
    return null;
}

export default function FileListAccordion({ files = [], fromFile }) {
    const history = useHistory();
    const { currentUserData } = useContext(UserContext);
    const { groups } = useContext(DatabaseContext);

    const linkedFiles = useMemo(
        () =>
            files.filter((file) =>
                shouldFileBeVisible(file, groups, currentUserData),
            ),
        [files, groups, currentUserData],
    );

    const openFile = (file) => {
        const type = file.type || "";
        let pathname = `/file/${file.key}`;

        if (type.includes("video")) pathname = `/video/${file.key}`;
        else if (type.includes("image")) pathname = `/image/${file.key}`;
        else if (type === "application/pdf") pathname = `/pdf/${file.key}`;
        else if (type === "link") pathname = `/link/${file.key}`;

        history.push({ pathname, file });
    };

    return (
        <Box component="section" aria-labelledby="event-resources-title">
            <Box
                sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                    mb: 2.25,
                }}
            >
                <Box>
                    <Typography
                        id="event-resources-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.15 }}
                    >
                        Event resources
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Everything shared for this event.
                    </Typography>
                </Box>
                {linkedFiles.length > 0 && (
                    <Chip
                        label={`${linkedFiles.length} resource${
                            linkedFiles.length === 1 ? "" : "s"
                        }`}
                        sx={{
                            fontWeight: 700,
                            color: "primary.dark",
                            backgroundColor: "rgba(0, 149, 61, 0.1)",
                        }}
                    />
                )}
            </Box>

            {linkedFiles.length === 0 ? (
                <Box
                    sx={{
                        py: { xs: 6, sm: 8 },
                        px: 3,
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 3,
                        textAlign: "center",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            mx: "auto",
                            mb: 1.5,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: 2.5,
                            color: "text.secondary",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 29 }} />
                    </Box>
                    <Typography variant="h6" component="p">
                        No resources to show
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        This event has no linked resources you can access.
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 2,
                    }}
                >
                    {linkedFiles.map((file) => {
                        const details = getFileTypeDetails(file);
                        const TypeIcon = details.icon;
                        const previewUrl = getPreviewUrl(file);
                        const isCurrentFile = fromFile?.key === file.key;

                        return (
                            <Card
                                key={file.key}
                                variant="outlined"
                                sx={{
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: 3,
                                    overflow: "hidden",
                                    borderColor: "divider",
                                    boxShadow:
                                        "0 2px 10px rgba(20, 35, 25, 0.045)",
                                    transition:
                                        "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        borderColor: "rgba(20, 35, 25, 0.17)",
                                        boxShadow:
                                            "0 9px 24px rgba(20, 35, 25, 0.09)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        aspectRatio: "16 / 9",
                                        minHeight: 150,
                                        display: "grid",
                                        placeItems: "center",
                                        overflow: "hidden",
                                        backgroundColor: details.background,
                                    }}
                                >
                                    {previewUrl ? (
                                        <Box
                                            component="img"
                                            src={previewUrl}
                                            alt=""
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "block",
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        <TypeIcon
                                            sx={{
                                                fontSize: 52,
                                                color: details.color,
                                                opacity: 0.9,
                                            }}
                                        />
                                    )}
                                    <Chip
                                        size="small"
                                        icon={<TypeIcon />}
                                        label={details.label}
                                        sx={{
                                            position: "absolute",
                                            left: 12,
                                            bottom: 12,
                                            height: 28,
                                            color: details.color,
                                            fontWeight: 800,
                                            backgroundColor:
                                                "background.paper",
                                            boxShadow:
                                                "0 2px 8px rgba(20, 35, 25, 0.12)",
                                            "& .MuiChip-icon": {
                                                color: details.color,
                                            },
                                        }}
                                    />
                                </Box>

                                <CardContent
                                    sx={{
                                        p: 2.25,
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        "&:last-child": { pb: 2.25 },
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        component="h3"
                                        title={file.displayName}
                                        sx={{
                                            fontSize: 20,
                                            lineHeight: 1.25,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {file.displayName ||
                                            "Untitled resource"}
                                    </Typography>

                                    <Box sx={{ mt: "auto", pt: 2 }}>
                                        {isCurrentFile ? (
                                            <Chip
                                                size="small"
                                                label="Currently viewing"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "text.secondary",
                                                    backgroundColor:
                                                        "action.hover",
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                onClick={() => openFile(file)}
                                                endIcon={<ArrowForwardIcon />}
                                                sx={{ px: 0 }}
                                            >
                                                Open resource
                                            </Button>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
